// Canonical auto-title generation for menu TEMPLATES. This is the SINGLE
// source of truth — the menus client and server both import it so a template
// titles identically everywhere (mirrors bundle-title.ts for bundles).
//
// Title format: `{ProgramLabel} {MenuLineName} {GradeSpan}`, e.g.
// "NSLP Fresh 9-12" or "CACFP Faves PK-8". Segments with no data are omitted.
// Packaging style is NOT part of the title (product decision 2026-07-19): it
// is a structured field (`packagingMode`) shown on the preview card's own
// "Packaging" row ("Unitized"/"Bulk"). Legacy names still carrying the old
// "… Indiv"/"… Bulk" suffix stay auto-managed — deriveMenuTemplateRetitle
// recognizes them and heals them onto the packaging-less title on the next
// title-relevant settings save; stored strings are otherwise left untouched.
//
// CUSTOM-line templates are NEVER auto-titled: their user-entered name IS the
// vendor's menu name ("Vic & Dean's", ...) — see menuPreviewTitleEyebrow in
// apps/menus/server/src/managers/menu-builder.ts. The builder returns null
// for them.

import { expandCombinedGradeRangeCode, normalizeGradeRangeCode } from './menu-grade-range-gate'

export { expandCombinedGradeRangeCode } from './menu-grade-range-gate'
import { MENU_LINE_LABELS, MENU_PROGRAM_LABELS, type MenuLineCode, type MenuProgramCode } from './menu-taxonomy'

/** Canonical youngest-to-oldest ordering of the CONSTITUENT grade-range
 * codes — the span builder expands combined bands (k-8 → k-5 + 6-8) before
 * checking contiguity, so this list stays at constituent granularity even
 * though k-5 / 6-8 are retired from pickers (portion-groups.ts). Sort a
 * mixed list with `menuGradeRangeSortIndex`, which places a combined band at
 * its first constituent's slot. Templates persist codes in the order the
 * user clicked the chips, so read paths sort. */
export const MENU_GRADE_RANGE_CODE_ORDER: readonly string[] = Object.freeze([
  'infant',
  'age-1-2',
  'pk3-5',
  'k-5',
  '6-8',
  '9-12',
  'adult',
])

/** Display labels per grade-range code. Owned here so the menus client and
 * server label maps cannot drift; clients re-import rather than redeclare. */
export const MENU_GRADE_RANGE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  infant: 'Infant',
  'age-1-2': 'PK Age 1-2',
  'pk3-5': 'PK Age 3-5',
  // k-8 is THE school-age band offered to pickers (2026-09-15). k-5 / 6-8
  // are retired from new selections but legacy rows still label.
  'k-8': 'K-8',
  'k-5': 'K-5',
  '6-8': '6-8',
  '9-12': '9-12',
  adult: 'Adult',
})

/**
 * Sort index of any grade-range code (raw spelling accepted) in the canonical
 * youngest-to-oldest order. Combined bands sort at their FIRST constituent's
 * slot (k-8 and k-12 with k-5, pk with age-1-2); unknown codes sort last.
 */
export function menuGradeRangeSortIndex(code: string): number {
  const constituents = expandCombinedGradeRangeCode(normalizeGradeRangeCode(code))
  const index = MENU_GRADE_RANGE_CODE_ORDER.indexOf(constituents[0] ?? '')
  return index === -1 ? MENU_GRADE_RANGE_CODE_ORDER.length : index
}

// Endpoints for collapsing a CONTIGUOUS run of grade ranges into one span
// ("PK-8", "K-12"). Only the school-grade codes participate — infant/adult
// (and the PK age bands other than pk3-5's "PK") have no natural grade-number
// endpoints, so runs touching them fall back to joined labels.
const GRADE_SPAN_ENDPOINTS: Readonly<Record<string, { start: string; end: string }>> = Object.freeze({
  'pk3-5': { start: 'PK', end: 'PK' },
  'k-5': { start: 'K', end: '5' },
  '6-8': { start: '6', end: '8' },
  '9-12': { start: '9', end: '12' },
})

function gradeRangeSortIndex(code: string): number {
  const index = MENU_GRADE_RANGE_CODE_ORDER.indexOf(code)
  return index === -1 ? MENU_GRADE_RANGE_CODE_ORDER.length : index
}

function gradeRangeLabel(code: string): string {
  return MENU_GRADE_RANGE_LABELS[code] ?? code
}

/**
 * Human label for a set of grade-range codes, as used inside template titles:
 * - single code → its label ("9-12", "K-5", "PK Age 3-5")
 * - contiguous run of school-grade codes → collapsed span ("PK-8", "K-12")
 * - anything else (non-contiguous, or runs touching infant/age-1-2/adult/
 *   unknown codes) → individual labels joined with " + "
 * - empty → null (segment omitted)
 */
export function buildGradeRangeSpanLabel(gradeRangeCodes: readonly string[] | null | undefined): string | null {
  const codes = Array.from(
    new Set((gradeRangeCodes ?? []).map(normalizeGradeRangeCode).filter(Boolean).flatMap(expandCombinedGradeRangeCode)),
  ).sort((left, right) => gradeRangeSortIndex(left) - gradeRangeSortIndex(right))
  if (codes.length === 0) {
    return null
  }
  if (codes.length === 1) {
    return gradeRangeLabel(codes[0])
  }
  const indices = codes.map(gradeRangeSortIndex)
  const contiguous =
    indices.every((index) => index < MENU_GRADE_RANGE_CODE_ORDER.length) &&
    indices.every((index, position) => position === 0 || index === indices[position - 1] + 1)
  if (contiguous) {
    const start = GRADE_SPAN_ENDPOINTS[codes[0]]?.start
    const end = GRADE_SPAN_ENDPOINTS[codes[codes.length - 1]]?.end
    if (start && end) {
      return `${start}-${end}`
    }
  }
  return codes.map(gradeRangeLabel).join(' + ')
}

// Legacy packaging suffixes from the retired hand-naming convention
// ("NSLP Fresh 9-12 Indiv"). No longer COMPOSED into titles, but retitle
// comparison still recognizes them so pre-existing auto-managed names heal.
const LEGACY_PACKAGING_TITLE_SUFFIXES: readonly string[] = Object.freeze(['indiv', 'bulk'])

export interface MenuTemplateAutoTitleInput {
  /** Program codes; the PRIMARY program (first entry) drives the title. */
  programs?: readonly string[] | null
  menuLine?: { code?: string | null; name?: string | null } | null
  gradeRangeCodes?: readonly string[] | null
}

/**
 * Settings-derived template title, e.g. "NSLP Fresh 9-12". Returns null
 * for CUSTOM-line templates (their name is the vendor name) and when no
 * segment resolves — callers fall back to their own default in both cases.
 * Packaging style is deliberately NOT a title segment — it lives on the
 * preview card's Packaging row, keyed off the stored `packagingMode` field.
 */
export function buildMenuTemplateAutoTitle(input: MenuTemplateAutoTitleInput): string | null {
  const menuLineCode = input.menuLine?.code?.trim().toUpperCase() ?? null
  if (menuLineCode === 'CUSTOM') {
    return null
  }
  const primaryProgram = (input.programs ?? []).find((program) => program.trim() !== '')?.trim()
  const programLabel = primaryProgram
    ? (MENU_PROGRAM_LABELS[primaryProgram as MenuProgramCode] ?? primaryProgram)
    : null
  const menuLineName =
    input.menuLine?.name?.trim() || (menuLineCode ? (MENU_LINE_LABELS[menuLineCode as MenuLineCode] ?? null) : null)
  const gradeSpan = buildGradeRangeSpanLabel(input.gradeRangeCodes)
  const title = [programLabel, menuLineName, gradeSpan]
    .filter((segment): segment is string => Boolean(segment))
    .join(' ')
  return title === '' ? null : title
}

export interface MenuTemplateRetitleInput {
  /** The template's current stored name. */
  currentName: string | null | undefined
  /** Auto-title computed from the settings BEFORE the update. */
  previousAutoTitle: string | null | undefined
  /** Auto-title computed from the settings AFTER the update. */
  nextAutoTitle: string | null | undefined
}

function normalizeTitleForComparison(value: string): string {
  return value.trim().replace(/\s+/gu, ' ').toLowerCase()
}

// A name still matching the previous auto title once a legacy packaging
// suffix (" Indiv"/" Bulk" — the retired title convention) is stripped is
// treated as auto-managed, so pre-2026-07-19 names heal onto the
// packaging-less titles instead of being frozen as "hand-typed".
function matchesAutoTitle(currentName: string, autoTitle: string): boolean {
  const normalizedName = normalizeTitleForComparison(currentName)
  const normalizedAuto = normalizeTitleForComparison(autoTitle)
  if (normalizedName === normalizedAuto) {
    return true
  }
  return LEGACY_PACKAGING_TITLE_SUFFIXES.some((suffix) => normalizedName === `${normalizedAuto} ${suffix}`)
}

/**
 * Decide whether a settings change should rename the template. A name is
 * "auto-managed" when it still equals the previous settings-derived title
 * (case/whitespace-insensitive, tolerating a legacy " Indiv"/" Bulk"
 * packaging suffix) OR is a legacy duplication stamp ("Copy of ..." — such
 * prod rows self-heal onto the auto title on their next settings save).
 * Hand-typed names are never touched. Returns the new name to persist, or
 * undefined to leave the name alone (including when the name is already the
 * next auto title).
 */
export function deriveMenuTemplateRetitle(input: MenuTemplateRetitleInput): string | undefined {
  const nextAutoTitle = input.nextAutoTitle?.trim()
  const currentName = input.currentName?.trim() ?? ''
  if (!nextAutoTitle || normalizeTitleForComparison(currentName) === normalizeTitleForComparison(nextAutoTitle)) {
    return undefined
  }
  const previousAutoTitle = input.previousAutoTitle?.trim()
  const autoManaged =
    (previousAutoTitle != null && previousAutoTitle !== '' && matchesAutoTitle(currentName, previousAutoTitle)) ||
    /^copy of /iu.test(currentName)
  return autoManaged ? nextAutoTitle : undefined
}
